import * as React from "react";
import type { Sizing, Template, TemplateItemInterface } from "src/components/template/types";
import { useTemplateViewEdit } from "src/components/providers/template-view-edit-provider";

export const TemplateDrawerContext = React.createContext<{
    drawerOpen: boolean;
    setDrawerOpen: React.Dispatch<boolean>;
    onEditItem:
        | {
              isChart: boolean;
              item: TemplateItemInterface | { sizing: Sizing };
              templateIndex: number | undefined;
              rowIndex: number | undefined;
              itemIndex: number | undefined;
          }
        | undefined;
    setOnEditItem: React.Dispatch<
        | {
              isChart: boolean;
              item: TemplateItemInterface | { sizing: Sizing };
              templateIndex: number | undefined;
              rowIndex: number | undefined;
              itemIndex: number | undefined;
          }
        | undefined
    >;
    template: Template | undefined;
}>({
    onEditItem: undefined,
    drawerOpen: false,
    setDrawerOpen: (_) => {},
    setOnEditItem: (_) => {},
    template: undefined,
});

export const TemplateDrawerProvider = ({ children }: { children: React.ReactNode }) => {
    const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
    const [onEditItem, setOnEditItem] = React.useState<
        | {
              isChart: boolean;
              item: TemplateItemInterface | { sizing: Sizing };
              templateIndex: number | undefined;
              rowIndex: number | undefined;
              itemIndex: number | undefined;
          }
        | undefined
    >(undefined);

    const { dashboard } = useTemplateViewEdit();

    const getTemplate = () => {
        if (!dashboard || !dashboard.templates || !dashboard.templates.length) return undefined;
        if (!onEditItem || onEditItem.templateIndex === undefined) return undefined;
        return dashboard.templates[onEditItem.templateIndex] as Template;
    };

    const value = React.useMemo(
        () => ({
            drawerOpen,
            setDrawerOpen,
            setOnEditItem,
            onEditItem,
            template: getTemplate(),
        }),
        [drawerOpen, onEditItem]
    );

    return (
        <TemplateDrawerContext.Provider value={value}>{children}</TemplateDrawerContext.Provider>
    );
};

export const useTemplateDrawer = () => {
    const context = React.useContext(TemplateDrawerContext);
    if (context === undefined) {
        throw new Error(`useTemplateDrawer must be used within a TemplateDrawerContext`);
    }
    return context;
};
